import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { copyToClipboard } from "@/lib/utils";

interface CopyableIdProps {
  value: string;
  label?: string;
}

/**
 * Displays a MongoDB id in monospace with a copy-to-clipboard button.
 * Used on super-admin detail pages so OPS can grab entity ids in one click.
 */
export function CopyableId({ value, label }: CopyableIdProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (await copyToClipboard(value)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div>
      {label && <span className="text-muted-foreground text-sm">{label}</span>}
      <div className="flex items-center gap-2 mt-1">
        <p className="font-mono text-sm bg-muted p-2 rounded break-all flex-1">
          {value}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={handleCopy}
          aria-label={copied ? "Copied" : "Copy ID"}
          title={copied ? "Copied" : "Copy ID"}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
